import { motion } from 'framer-motion';
import { AnimatedNumber } from '../../../../components/AnimatedNumber';
import { CARD_CLASS, OMS_COLORS } from '../../dashboard/omsDashTheme';
import { DeltaBadge, MiniSpark } from './adminBits';

export interface AdminKpi {
  key: string;
  label: string;
  value: number;
  format: (n: number) => string;
  /** Signed delta vs previous window (already in display units). */
  delta: number | null;
  deltaSuffix: string;
  /** Honest definition incl. denominator, shown as the title tooltip. */
  definition: string;
  spark: number[];
}

interface Props {
  kpis: AdminKpi[];
  phrase: string;
  loading: boolean;
}

/**
 * Six platform KPIs as typographic stat plates — value counts up once,
 * sparkline draws once, every figure carries its definition on hover.
 */
export default function KpiStrip({ kpis, phrase, loading }: Props) {
  if (loading && kpis.length === 0) {
    return (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i} className="h-[92px] animate-pulse rounded-2xl bg-[rgba(11,59,46,0.06)]" />
        ))}
      </div>
    );
  }
  return (
    <section aria-label="Platform KPIs" className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
      {kpis.map((kpi, index) => (
        <motion.div
          key={kpi.key}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.06 * index, duration: 0.35 }}
          title={kpi.definition}
          className={`${CARD_CLASS} px-3.5 py-3`}
        >
          <p className="text-[9px] font-bold uppercase tracking-[0.16em]" style={{ color: OMS_COLORS.caption }}>
            {kpi.label}
          </p>
          <p className="mt-1 text-2xl font-extrabold leading-none tabular-nums" style={{ color: OMS_COLORS.tealDark }}>
            <AnimatedNumber value={kpi.value} format={kpi.format} />
          </p>
          <div className="mt-1.5 flex items-end justify-between gap-2">
            <DeltaBadge delta={kpi.delta} phrase={phrase} suffix={kpi.deltaSuffix} />
            <MiniSpark values={kpi.spark} width={64} height={18} label={`${kpi.label} trend`} />
          </div>
        </motion.div>
      ))}
    </section>
  );
}
